import { createContext, useContext } from 'react';

import type { ExportMediaAssetDialogProps } from '@/dialogs/export-media-asset/ExportMediaAssetDialog';
import type { ItemEditorDialogProps } from '@/dialogs/item-editor/ItemEditorDialog';
import type { MassUpdateDialogProps } from '@/dialogs/mass-update/MassUpdateDialog';
import type { PriorItemDialogProps } from '@/dialogs/prior-item/PriorItemDialog';
import type { PersistedItem } from '@/types/DataModel';

export type OpenItemEditorParams = Omit<ItemEditorDialogProps, 'onClose' | 'openItemEditor'> & {
    onSaved?: (item?: PersistedItem) => void;
};

export type OpenMassUpdateParams = Omit<MassUpdateDialogProps, 'onClose'> & {
    onCompleted?: () => void;
};

export type OpenPriorItemParams = Omit<PriorItemDialogProps, 'onClose'> & {
    onSubmit?: (values?: object) => void;
};

export type OpenMediaAssetExportParams = Omit<ExportMediaAssetDialogProps, 'onClose'>;

export type OpenCompareOptions = {
    currentLayout?: string;
};

export type DialogHostApi = {
    openItemEditor: (props: OpenItemEditorParams) => void;
    openCompare: (items: unknown[], options?: OpenCompareOptions) => void;
    openMassUpdate: (props: OpenMassUpdateParams) => void;
    openPriorItem: (props: OpenPriorItemParams) => void;
    openMediaAssetExport: (props: OpenMediaAssetExportParams) => void;
    closeAll: () => void;
};

const DialogHostContext = createContext<DialogHostApi | undefined>(undefined);

const useDialogHost = (): DialogHostApi => {
    const dialogHost = useContext(DialogHostContext);

    if (!dialogHost) {
        throw new Error('useDialogHost must be used within a DialogHost');
    }

    return dialogHost;
};

export { DialogHostContext, useDialogHost };
